import { useId } from "react"
import { ShieldCheck } from "lucide-react"

import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { cn } from "@/lib/utils"
import { RATES } from "@/lib/post/options"

// The QA/QC transparency formula figures are PriceList-driven and Insider-aware
// (base_posting.rb resolves them via price_list.rate_for(:addon_cost, …)), so
// they read the server-resolved ctx.rates map with the RATES seed as the
// no-live-PriceList fallback — never a hardcoded literal (Plumbline WI-A10,
// tuw-app:P-001). The non-price words ("50,000 words", "per 1,000 words") are
// business constants and stay literal.
function addonFormulaRates(ctx = {}) {
  const seed = RATES.ADDON_COST
  const r = (ctx.rates && ctx.rates.addon_cost) || {}
  return {
    qaBase: r.qa_base ?? seed.qa_base,
    qaPer: r.qa_per_thousand ?? seed.qa_per_thousand,
    qcRate: r.quality_control ?? seed.quality_control,
  }
}

const usd = (n) => `$${Number(n).toFixed(2)}`

// AddonStep — the single shared "Management & review" step (CONFIG-SCHEMA §5).
//
// These are JOB-LEVEL singletons on the parent job_postings row (one per
// project, not per service).
//
// Props:
//   state          — the wizard state (reads state.managed_services_tier,
//                    state.quality_assurance_addon, state.quality_control_enabled,
//                    state.managed_service_details)
//   setField(path, value) — the setField CONTRACT for this step:
//                    * job-level keys are written with a bare key:
//                        setField("managed_services_tier", "managed")
//                        setField("quality_assurance_addon", "selected" | "declined")
//                        setField("quality_control_enabled", "selected" | "declined")
//                    * questionnaire answers are written with a DOTTED path so the
//                      page nests them under managed_service_details:
//                        setField("managed_service_details.freelancer_selection", "1")
//                    The page is responsible for interpreting the dotted path.
//   eligibleQaQc   — bool; QA/QC sections shown only when true
//   managedAllowed — bool; hides the Managed tier card when false
//   managedTiers   — [{ value, label, desc?, from? }] (from options.MANAGED_TIERS + addon.js floors)
//   addonLines     — { qa?: number, qc?: number } resolved amounts for display
//   ctx            — ctx.options is the options.js module (QUESTIONNAIRE_OPTIONS etc.)
const money = (n) =>
  typeof n === "number"
    ? `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : null

function SectionHead({ title, amount }) {
  return (
    <div className="mb-1 flex items-baseline justify-between gap-3">
      <h3 className="font-display text-[15px] font-bold tracking-tight text-foreground">{title}</h3>
      {money(amount) && (
        <span className="whitespace-nowrap font-display text-[12.5px] font-bold text-muted-foreground">
          {money(amount)}
        </span>
      )}
    </div>
  )
}

const CARD_BASE = "flex w-full items-start gap-3 rounded-xl border px-4 py-3.5 text-left transition-colors"
const CARD_ON = "border-primary bg-[var(--red-soft)]"
const CARD_OFF = "border-line-2 hover:border-ink-3"

// Option-card radio that tolerates string AND boolean option values (the
// questionnaire mixes both; Radix RadioGroup emits strings, so map the selected
// string back to the option's original value before updating state).
function CardRadio({ label, help, required, options, value, onChange, fieldKey, groupLabel }) {
  const groupId = useId()
  const labelId = label ? `${groupId}-label` : undefined
  return (
    <div className="flex flex-col gap-2" data-post-field={fieldKey}>
      <div id={labelId} className="flex items-center gap-2 text-[12.5px] font-bold text-foreground">
        {label}
        {required && (
          <span className="rounded-full bg-[var(--red-soft)] px-2 py-0.5 text-[11px] font-semibold text-primary">
            Required
          </span>
        )}
      </div>
      {help && <p className="-mt-0.5 text-[12px] text-muted-foreground">{help}</p>}
      <RadioGroup
        value={value == null ? "" : String(value)}
        onValueChange={(next) => onChange(options.find((o) => String(o.value) === next)?.value ?? next)}
        aria-labelledby={labelId}
        aria-label={labelId ? undefined : groupLabel}
        className="gap-2.5"
      >
        {options.map((o, index) => {
          const on = value === o.value
          const optionId = `${groupId}-${index}`
          return (
            <label
              key={String(o.value)}
              htmlFor={optionId}
              className={cn(CARD_BASE, "cursor-pointer", on ? CARD_ON : CARD_OFF)}
            >
              <RadioGroupItem id={optionId} value={String(o.value)} aria-label={o.label} className="mt-0.5" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
                  <span className="font-display text-[14px] font-bold text-foreground">{o.label}</span>
                  {o.price && (
                    <span className="basis-full whitespace-nowrap font-display text-[12.5px] font-bold text-muted-foreground sm:ml-auto sm:basis-auto">
                      {o.price}
                    </span>
                  )}
                </div>
                {o.sub && <p className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">{o.sub}</p>}
              </div>
            </label>
          )
        })}
      </RadioGroup>
    </div>
  )
}

function LockedCard({ children }) {
  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-done/40 bg-done/10 px-4 py-3.5 text-[13px] font-semibold text-foreground">
      <ShieldCheck className="size-4 shrink-0 text-done" strokeWidth={2.4} />
      <span>{children}</span>
    </div>
  )
}

function WaiverCheck({ label, checked, onChange, error }) {
  const id = useId()
  const errorId = `${id}-error`
  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor={id}
        className={cn(CARD_BASE, "cursor-pointer items-start", checked ? CARD_ON : CARD_OFF)}
      >
        <Checkbox
          id={id}
          checked={checked}
          onCheckedChange={(next) => onChange(next === true)}
          aria-invalid={!!error}
          aria-describedby={error ? errorId : undefined}
          className="mt-0.5"
        />
        <span className="text-[12.5px] font-medium leading-snug text-foreground">{label}</span>
      </label>
      {error && <p id={errorId} className="text-[11.5px] font-semibold text-destructive">{error}</p>}
    </div>
  )
}

export function AddonStep({
  state = {},
  setField,
  eligibleQaQc = false,
  managedAllowed = true,
  managedTiers = [],
  addonLines = {},
  ctx = {},
}) {
  const tier = state.managed_services_tier || "self_managed"
  const managed = tier === "managed"
  const details = state.managed_service_details || {}

  const QO = (ctx.options && ctx.options.QUESTIONNAIRE_OPTIONS) || {}
  const setMs = (key, value) => setField(`managed_service_details.${key}`, value)

  // Server-traced QA/QC formula figures (Insider-aware; see addonFormulaRates).
  const { qaBase, qaPer, qcRate } = addonFormulaRates(ctx)
  const qaFormula = `${usd(qaBase)} for up to 50,000 words, then ${usd(qaPer)} per additional 1,000.`
  const qcFormula = `${usd(qcRate)} per 1,000 words.`

  const tiers = managedTiers.filter((t) => t.value !== "managed" || managedAllowed)

  const selectTier = (v) => {
    if (v === tier) return
    setField("managed_services_tier", v)
    if (eligibleQaQc) {
      if (v === "managed") {
        setField("quality_assurance_addon", "selected")
        setField("quality_control_enabled", "selected")
      } else {
        // Reset to the explicit "declined" (a VALID enum value), never null:
        // Continue isn't gated on QA/QC, and submit's quality_assurance__one
        // 422s on a null selection for QA-eligible work (Plumbline bug 5).
        setField("quality_assurance_addon", "declined")
        setField("quality_control_enabled", "declined")
      }
    }
  }

  return (
    <div className="flex flex-col">
      {/* Managed Services */}
      <section className="border-b border-border pb-5" data-post-field="managed_services_tier">
        <SectionHead title="Managed Services" />
        <p className="mb-3 max-w-[600px] text-[12.5px] leading-relaxed text-muted-foreground text-pretty">
          Hand the project-management work to a dedicated TUW Project Manager, or keep full control yourself.
          Add-ons must be chosen now. They cannot be added after the project is created.
        </p>
        <RadioGroup
          value={tier}
          onValueChange={selectTier}
          aria-label="Managed Services"
          className="gap-2.5"
        >
          {tiers.map((t, index) => {
            const on = tier === t.value
            const optionId = `managed-tier-${index}`
            return (
              <label
                key={t.value}
                htmlFor={optionId}
                className={cn(CARD_BASE, "cursor-pointer", on ? CARD_ON : CARD_OFF)}
              >
                <RadioGroupItem id={optionId} value={t.value} aria-label={t.label} className="mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
                    <span className="font-display text-[14px] font-bold text-foreground">{t.label}</span>
                    {t.value === "managed" && (
                      <span className="rounded-md bg-done/15 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide text-done">
                        Incl. QA + QC
                      </span>
                    )}
                    {money(t.from) && (
                      <span className="basis-full whitespace-nowrap font-display text-[12.5px] font-bold text-muted-foreground sm:ml-auto sm:basis-auto">
                        From {money(t.from)}
                      </span>
                    )}
                  </div>
                  {t.desc && (
                    <p className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">{t.desc}</p>
                  )}
                </div>
              </label>
            )
          })}
        </RadioGroup>

        {managed && (
          <div className="mt-5 flex flex-col gap-5 rounded-xl border border-border bg-accent px-4 py-4">
            <p className="text-[12px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
              Managed questionnaire
            </p>

            <CardRadio
              fieldKey="freelancer_selection"
              label="How should we choose your freelancer?"
              required
              options={QO.freelancer_selection || []}
              value={details.freelancer_selection}
              onChange={(v) => setMs("freelancer_selection", v)}
            />

            <div className="flex flex-col gap-2" data-post-field="freelancer_note">
              <label className="flex items-center gap-2 text-[12.5px] font-bold text-foreground">
                Requirements and non-negotiables
                <span className="rounded-full bg-[var(--red-soft)] px-2 py-0.5 text-[11px] font-semibold text-primary">
                  Required
                </span>
              </label>
              <Textarea
                rows={4}
                value={details.freelancer_note ?? ""}
                placeholder="Tell your Project Manager what matters most for this project."
                onChange={(e) => setMs("freelancer_note", e.target.value)}
              />
            </div>

            <CardRadio
              fieldKey="pre_scheduling"
              label="Scheduling preference"
              required
              options={QO.pre_scheduling || []}
              value={details.pre_scheduling}
              onChange={(v) => setMs("pre_scheduling", v)}
            />

            <CardRadio
              fieldKey="progress_updates"
              label="How often would you like progress updates?"
              required
              options={QO.progress_updates || []}
              value={details.progress_updates}
              onChange={(v) => setMs("progress_updates", v)}
            />

            <CardRadio
              fieldKey="zoom_call"
              label="Zoom calls"
              required
              options={QO.zoom_call_managed || []}
              value={details.zoom_call}
              onChange={(v) => setMs("zoom_call", v)}
            />

            <CardRadio
              fieldKey="additional_services"
              label="Additional service options"
              required
              options={QO.additional_services || []}
              value={details.additional_services}
              onChange={(v) => setMs("additional_services", v)}
            />

            <div className="flex flex-col gap-2">
              <label className="text-[12.5px] font-bold text-foreground">Acknowledgements</label>
              <div data-post-field="quality_waiver">
                <WaiverCheck
                  label="Please acknowledge that, should you opt out/fail to review your content and/or request revisions within the allotted timeframes, TUW is not responsible for any changes, barring any major quality issues or failure on the freelancer's part to adhere to the project's initial requirements."
                  checked={!!details.quality_waiver}
                  onChange={(v) => setMs("quality_waiver", v)}
                  error={!details.quality_waiver ? "Acknowledge the content review responsibility to continue." : null}
                />
              </div>
              <div data-post-field="timeframe_waiver">
                <WaiverCheck
                  label="Please acknowledge that TUW is not responsible for any additional changes or revisions to the content upon completion/fulfillment, barring any major quality issues or failure on the freelancer's part to adhere to the project's initial requirements."
                  checked={!!details.timeframe_waiver}
                  onChange={(v) => setMs("timeframe_waiver", v)}
                  error={!details.timeframe_waiver ? "Acknowledge the changes after completion policy to continue." : null}
                />
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Quality Assurance */}
      {eligibleQaQc && (
        <section className="border-b border-border py-5" data-post-field="quality_assurance_addon">
          <SectionHead title="Quality Assurance" amount={addonLines.qa} />
          <p className="mb-3 max-w-[600px] text-[12.5px] leading-relaxed text-muted-foreground text-pretty">
            A QA editor reviews your order before you approve it, checking writing quality, tone, and
            adherence to the brief and outline. {qaFormula}
          </p>
          {managed ? (
            <LockedCard>Included with Managed{money(addonLines.qa) ? ` — ${money(addonLines.qa)}` : ""}</LockedCard>
          ) : (
            <CardRadio
              groupLabel="Quality Assurance"
              options={[
                {
                  value: "selected",
                  label: "Add Quality Assurance",
                  price: money(addonLines.qa),
                  sub: "A QA editor reviews your order before approval.",
                },
                { value: "declined", label: "No, thanks", sub: "I will review my order without assistance." },
              ]}
              value={state.quality_assurance_addon}
              onChange={(v) => setField("quality_assurance_addon", v)}
            />
          )}
        </section>
      )}

      {/* Quality Control */}
      {eligibleQaQc && (
        <section className="pt-5">
          <SectionHead title="Quality Control" amount={addonLines.qc} />
          <p className="mb-3 max-w-[600px] text-[12.5px] leading-relaxed text-muted-foreground text-pretty">
            A QC editor does a final pass once all content and revisions are complete, catching typos, spacing,
            grammar, mechanics, APA style and formatting. {qcFormula}
          </p>
          {managed ? (
            <LockedCard>Included with Managed{money(addonLines.qc) ? ` — ${money(addonLines.qc)}` : ""}</LockedCard>
          ) : (
            <CardRadio
              groupLabel="Quality Control"
              options={[
                {
                  value: "selected",
                  label: "Add Quality Control",
                  price: money(addonLines.qc),
                  sub: "A QC editor gives your order a final review.",
                },
                { value: "declined", label: "No Quality Control", sub: "I will manage the final review myself." },
              ]}
              value={state.quality_control_enabled}
              onChange={(v) => setField("quality_control_enabled", v)}
            />
          )}
        </section>
      )}
    </div>
  )
}

export { CardRadio as AddonCardRadio }
